/**
 * Labstep
 *
 * @module grid/SmartTableProtocolValue/components/ToolPanel
 * @desc Entity DataGrid dropdown to set column options
 */

import type { Column } from 'ag-grid-community';
import EntityViewActionDelete from 'labstep-web/components/EntityView/Action/Delete';
import { withAuthenticatedUserEntity } from 'labstep-web/containers/AuthenticatedUser';
import {
  useDataGridContext,
  useGridSmartTableProtocolValueServiceFromContext,
} from 'labstep-web/contexts/grid/hook';
import { ActionComponent } from 'labstep-web/core/Action/Component';
import { Flex } from 'labstep-web/core/Flex';
import { Header } from 'labstep-web/core/Header';
import { useToolPanelContext } from 'labstep-web/grid/Index/components/ToolPanel/context';
import { getMetadataColDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/metadata';
import { EntityDataGridToolPanelAddColumn } from 'labstep-web/grid/SmartTableProtocolValue/components/ToolPanel/AddColumn';
import { EntityDataGridToolPanelMetadataMolecule } from 'labstep-web/grid/SmartTableProtocolValue/components/ToolPanel/MetadataMolecule';
import { EntityDataGridToolPanelProtocolValue } from 'labstep-web/grid/SmartTableProtocolValue/components/ToolPanel/ProtocolValue';
import { EntityDataGridToolPanelSection } from 'labstep-web/grid/SmartTableProtocolValue/components/ToolPanel/Section';
import { SectionType } from 'labstep-web/grid/services/grid.service';
import type { Metadata } from 'labstep-web/models/metadata';
import { MetadataType } from 'labstep-web/models/metadata/types';
import type { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './styles.module.scss';
import type { IEntityDataGridToolPanelProps } from './types';
import {
  filterHiddenColumnDefs,
  filterPinnedLeftColumnDefs,
  filterPinnedRightColumnDefs,
  filterShownAndNotPinnedPositionColumnDefs,
  getColIdArr,
} from './utils';

export enum ToolPanelState {
  default = 'default',
  metadata_molecule = 'metadata_molecule',
  protocol_value = 'protocol_value',
  add_column = 'add_column',
}

export const EntityDataGridToolPanel: React.FC<
  IEntityDataGridToolPanelProps
> = ({
  entityView,
  experimentOrProtocol,
  authenticatedUserEntity,
  fullscreen,
}) => {
  const [metadataMolecule, setMetadataMolecule] =
    useState<Metadata | null>(null);

  const gridSmartTableProtocolValueService =
    useGridSmartTableProtocolValueServiceFromContext();

  const ctx = useToolPanelContext();

  if (!ctx) {
    return null;
  }

  const { toolPanelState, setToolPanelState, activeVariable } = ctx;

  const { agGrid, columns, columnState } = useDataGridContext();
  if (!columns) {
    return null;
  }

  const colIds =
    gridSmartTableProtocolValueService?.getColumnDefinitionIds();

  const [protocolValue, setProtocolValue] =
    useState<ProtocolValue | null>(null);

  /**
   * Columns by type (pinned left, pinned right, shown, hidden)
   */
  const columnsByType: Record<
    'pinnedLeft' | 'pinnedRight' | 'shown' | 'hidden',
    Column[]
  > = useMemo(
    () => ({
      pinnedLeft: filterPinnedLeftColumnDefs(columns, columnState),
      pinnedRight: filterPinnedRightColumnDefs(columns, columnState),
      shown: filterShownAndNotPinnedPositionColumnDefs(
        columns,
        columnState,
      ),
      hidden: filterHiddenColumnDefs(columns, columnState),
    }),
    [columns, columnState],
  );

  useEffect(() => {
    if (
      agGrid &&
      !agGrid.api.isSideBarVisible() &&
      toolPanelState !== ToolPanelState.default
    ) {
      setToolPanelState(ToolPanelState.default);
    }
  }, [agGrid]);
  const onSortEnd = useCallback(
    (columnsSorted: Column[]) => {
      if (agGrid) {
        const allColumns = [
          ...columnsByType.pinnedLeft,
          ...columnsSorted,
          ...columnsByType.pinnedRight,
          ...columnsByType.hidden,
        ];
        const allColumnsColIdArr = getColIdArr(allColumns);
        agGrid.columnApi.applyColumnState({
          state: allColumnsColIdArr,
          applyOrder: true,
        });
      }
    },
    [agGrid],
  );

  const toggleColumnVisibleByColId = useCallback(
    (colId) => {
      if (agGrid) {
        gridSmartTableProtocolValueService?.toggleColumnVisible(
          agGrid,
          colId,
        );
      }
    },
    [agGrid, gridSmartTableProtocolValueService],
  );

  const selectMetadata = (metadata: Metadata) => {
    if (metadata.type === MetadataType.molecule) {
      setMetadataMolecule(metadata);
      setToolPanelState(ToolPanelState.metadata_molecule);
      return;
    }
    if (gridSmartTableProtocolValueService) {
      const colDef = getMetadataColDef(
        experimentOrProtocol,
        metadata.parent_entity_class,
        metadata.type,
        metadata.label || '',
      );
      gridSmartTableProtocolValueService.addColumnDef(colDef);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.close}>
        <ActionComponent
          type="icon"
          icon="times"
          onClick={() => {
            agGrid?.api?.setSideBarVisible(false);
            setToolPanelState(ToolPanelState.default);
          }}
        />
      </div>
      {toolPanelState === ToolPanelState.default && (
        <>
          <Header size="tiny" noMargin>
            Columns
          </Header>
          <Flex column grow style={{ width: '100%' }}>
            <EntityDataGridToolPanelSection
              section={SectionType.visible}
              columns={columnsByType.shown}
              onToggleColumnVisible={toggleColumnVisibleByColId}
              onSortColumns={onSortEnd}
            />
          </Flex>
          <Flex
            style={{
              height: '30px',
              justifyContent: 'space-between',
            }}
          >
            <ActionComponent
              icon="plus"
              type="text"
              text="Add Column"
              onClick={() =>
                setToolPanelState(ToolPanelState.add_column)
              }
            />
            {entityView?.id && (
              <EntityViewActionDelete entityView={entityView} />
            )}
          </Flex>
        </>
      )}
      {gridSmartTableProtocolValueService &&
        toolPanelState === ToolPanelState.add_column &&
        colIds && (
          <EntityDataGridToolPanelAddColumn
            authenticatedUserEntity={authenticatedUserEntity}
            colIds={colIds}
            entityView={entityView}
            selectMetadata={selectMetadata}
            setProtocolValue={setProtocolValue}
            experimentOrProtocol={experimentOrProtocol}
            gridSmartTableProtocolValueService={
              gridSmartTableProtocolValueService
            }
          />
        )}
      {gridSmartTableProtocolValueService &&
        toolPanelState === ToolPanelState.metadata_molecule &&
        metadataMolecule &&
        protocolValue &&
        colIds && (
          <EntityDataGridToolPanelMetadataMolecule
            authenticatedUserEntity={authenticatedUserEntity}
            colIds={colIds}
            entityView={entityView}
            gridSmartTableProtocolValueService={
              gridSmartTableProtocolValueService
            }
            metadata={metadataMolecule}
            protocolValue={protocolValue}
            experimentOrProtocol={experimentOrProtocol}
          />
        )}
      {gridSmartTableProtocolValueService &&
        toolPanelState === ToolPanelState.protocol_value &&
        colIds && (
          <EntityDataGridToolPanelProtocolValue
            colIds={colIds}
            gridSmartTableProtocolValueService={
              gridSmartTableProtocolValueService
            }
            entityView={entityView}
            experimentOrProtocol={experimentOrProtocol}
          />
        )}
    </div>
  );
};

export default withAuthenticatedUserEntity(EntityDataGridToolPanel);
